import { useEffect, useState, useCallback, useRef } from "react";
import { useCart, useLabor } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { LaborSelection } from "../../../../interfaces";
import useSelectedSupplier from "./useSelectedSupplier";
import { useLaborFormulaEvaluator } from "../../hooks";

export default function useLaborUpdate(
    measurements: any,
    laborSchema: any,
    _dependencies: any,
    cartItems: any,
    overrideCalculations: boolean = true,
) {
    const [selectedValues, setSelectedValues] = useState<Record<string, LaborSelection>>({});
    const [changeInitiated, setChangeInitiated] = useState<boolean>(false);
    const { evaluateFormula } = useLaborFormulaEvaluator();

    const { useCreate, useListByProjectId, useUpdate, useDelete } = useCart();
    const { useListLaborInventory } = useLabor();
    const { project } = useProject();
    const { selectedSupplier } = useSelectedSupplier();

    const { data: supplierInventory } = useListLaborInventory();

    const createCartSvc = useCreate();
    const listCartItemSvc = useListByProjectId();
    const updateCartSvc = useUpdate();
    const deleteCartSvc = useDelete();

    // Load initial cart items
    const loadInitialCartItems = useCallback(() => {
        if (!cartItems || cartItems.length === 0) return;

        const result: Record<string, LaborSelection> = {};
        cartItems.forEach((item: any) => {
            if (item.LaborServiceId) {
                result[item.LaborServiceId] = {
                    quantity: item.ItemCount || 0,
                    unitPrice: 0, // Will be updated by supplier inventory effect
                    supplierId: item.SupplierId,
                    supplierName: item.SupplierName,
                };
            }
        });

        setSelectedValues((prev) => {
            // Merge but prioritize cart items
            return { ...prev, ...result };
        });
    }, [cartItems]);

    useEffect(() => {
        loadInitialCartItems();
    }, [loadInitialCartItems]);

    // Apply formulas to get default quantities
    useEffect(() => {
        if (!measurements || !laborSchema || laborSchema.length === 0 || !overrideCalculations) return;

        const calculatedResults = evaluateFormula(measurements, laborSchema, {});

        setSelectedValues((prev) => {
            const updated = { ...prev };
            let hasChanges = false;

            laborSchema.forEach((service: any) => {
                const calculatedQty = calculatedResults[service.Id] || 0;
                const current = prev[service.Id];

                // If not in cart and not manually touched, use formula value
                if (!current && calculatedQty > 0) {
                    hasChanges = true;
                    updated[service.Id] = {
                        quantity: calculatedQty,
                        unitPrice: 0,
                    };
                }
            });

            return hasChanges ? updated : prev;
        });
    }, [measurements, laborSchema, evaluateFormula]);

    // Update prices when supplier or inventory changes
    useEffect(() => {
        if (!supplierInventory) return;

        setSelectedValues((prev) => {
            const updated = { ...prev };
            let hasChanges = false;

            Object.entries(prev).forEach(([serviceId, selection]) => {
                // If a specific supplier is selected for the project, use their rate
                // Otherwise, keep current or zero
                const targetSupplierId = selectedSupplier?.id;

                if (targetSupplierId) {
                    const rateItem = supplierInventory.find(
                        (inv: any) => inv.Supplier_Id === targetSupplierId && inv.LaborService_Id === serviceId,
                    );
                    const newPrice = rateItem?.UnitPrice || 0;

                    if (selection.unitPrice !== newPrice || selection.supplierId !== targetSupplierId) {
                        hasChanges = true;
                        updated[serviceId] = {
                            ...selection,
                            unitPrice: newPrice,
                            supplierId: targetSupplierId,
                            supplierName: selectedSupplier.name,
                        };
                    }
                }
            });

            return hasChanges ? updated : prev;
        });
    }, [selectedSupplier, supplierInventory]);

    const handleQuantityChange = useCallback((serviceId: string, event: "add" | "remove") => {
        const delta = event === "add" ? 1 : -1;
        setChangeInitiated(true);

        setSelectedValues((prev) => {
            const current = prev[serviceId] || { quantity: 0, unitPrice: 0 };
            const newQty = Math.max(0, current.quantity + delta);
            return {
                ...prev,
                [serviceId]: { ...current, quantity: newQty },
            };
        });
    }, []);

    const handleRemoveLaborItem = useCallback((serviceId: string) => {
        setChangeInitiated(true);
        setSelectedValues((prev) => {
            const updated = { ...prev };
            delete updated[serviceId];
            return updated;
        });
    }, []);

    // Sync to cart
    useEffect(() => {
        if (!changeInitiated || !project?.id) return;

        const syncToCart = async () => {
            try {
                const currentCart = await listCartItemSvc.mutateAsync({ ProjectId: project.id });
                const laborCartItems = currentCart.filter((item: any) => item.LaborServiceId);

                // 1. Delete items that are in cart but not in selectedValues
                for (const item of laborCartItems) {
                    if (!selectedValues[item.LaborServiceId] || selectedValues[item.LaborServiceId].quantity === 0) {
                        await deleteCartSvc.mutateAsync({ Id: item.Id });
                    }
                }

                // 2. Update or Create
                const toCreate = [];
                for (const [serviceId, selection] of Object.entries(selectedValues)) {
                    if (selection.quantity === 0) continue;

                    const existing = laborCartItems.find((item: any) => item.LaborServiceId === serviceId);
                    if (existing) {
                        if (existing.ItemCount !== selection.quantity || existing.SupplierId !== selection.supplierId) {
                            await updateCartSvc.mutateAsync({
                                Id: existing.Id,
                                ItemCount: selection.quantity,
                                supplierId: selection.supplierId,
                            });
                        }
                    } else {
                        toCreate.push({
                            ItemCount: selection.quantity,
                            LaborServiceId: serviceId,
                            SupplierId: selection.supplierId,
                        });
                    }
                }

                if (toCreate.length > 0) {
                    await createCartSvc.mutateAsync({ ProjectId: project.id, CartItems: toCreate });
                }
            } catch (error) {
                console.error("Cart sync error:", error);
            }
        };

        const timer = setTimeout(syncToCart, 500); // Debounce
        return () => clearTimeout(timer);
    }, [selectedValues, changeInitiated, project?.id]);

    return {
        selectedValues,
        computedValues: {}, // Derived from selectedValues in UI if needed
        handleQuantityChange,
        handleRemoveLaborItem,
    };
}
